import { useState } from "react";
import type { PlatformCloudAccess } from "@rca/sdk";

/**
 * 基盤の設定（顧客のクラウドに入るための、我々の側の資格）。
 *
 * ★ **これは基盤全体の設定で、テナントの設定ではない。** 全顧客の同意が
 * ここで指したアプリを通るので、見えるのは基盤の管理者だけ（口の側でも
 * 弾いている。画面で隠すだけにしない）。
 *
 * ★ **資格は入れるだけで、読み返せない**（AC-11）。入っているかと、
 * 顧客に渡す側の値（アプリの ID）しか出さない。
 */

export interface CloudAccessViewProps {
  readonly items: readonly PlatformCloudAccess[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly onSave: (cloud: string, setting: Record<string, string>) => void;
  readonly onRemove: (cloud: string) => void;
}

export function CloudAccessView({
  items,
  busy,
  error,
  onSave,
  onRemove,
}: CloudAccessViewProps): React.JSX.Element {
  return (
    <div className="stack">
      <h2>クラウドに入るための設定</h2>
      <p className="muted">
        お客様のクラウドを選べるようにするには、我々の側のアプリを 1 つ登録して、
        その ID と資格をここに入れます。★ <strong>お客様ごとには増えません</strong>
        （クラウドごとに 1 組）。
      </p>

      {error !== null && (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}

      {items.length === 0 ? (
        <p className="muted">設定できるクラウドがありません。</p>
      ) : (
        items.map((item) => (
          <CloudCard
            key={item.cloud}
            item={item}
            busy={busy}
            onSave={onSave}
            onRemove={onRemove}
          />
        ))
      )}
    </div>
  );
}

function CloudCard({
  item,
  busy,
  onSave,
  onRemove,
}: {
  readonly item: PlatformCloudAccess;
  readonly busy: boolean;
  readonly onSave: (cloud: string, setting: Record<string, string>) => void;
  readonly onRemove: (cloud: string) => void;
}): React.JSX.Element {
  const [values, setValues] = useState<Record<string, string>>({});

  /**
   * ★ **全部そろわないと押せない。**
   *
   * 一部だけ入ると「選択肢には出るのに verify で落ちる」——置き場所は
   * あとから変えられないので、そこで選んだお客様が詰む。
   */
  const ready = item.fields.every((field) => (values[field.name] ?? "").trim() !== "");

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>{item.label}</h3>
        <span className="muted">{item.configured ? "設定済み" : "未設定"}</span>
      </div>

      {/* ★ 秘密でない値だけを出す。何が入っているかは分かる必要がある
          （顧客に渡す値なので）が、資格は絶対に出さない。 */}
      {item.fields
        .filter((field) => !field.secret && item.open[field.name] !== undefined)
        .map((field) => (
          <p className="muted" style={{ margin: 0 }} key={`shown-${field.name}`}>
            {field.label}: <code>{item.open[field.name]}</code>
          </p>
        ))}

      {item.redirectUri !== "" && (
        <div className="stack">
          {/* ★ アプリ登録に貼る値。手で書かせると 1 文字違って弾かれ、
              しかも同意の直前まで進んでから落ちる。 */}
          <label htmlFor={`redirect-${item.cloud}`}>リダイレクト URI（アプリ登録に貼る）</label>
          <input id={`redirect-${item.cloud}`} type="text" readOnly value={item.redirectUri} />
        </div>
      )}
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        手順は <code>{item.docs}</code> にあります。
      </p>

      {/* ★ 欄は実装が名乗ったものだけ。画面に書くと、クラウドを足した
          ときにずれる（`secret` の取り違えは資格の露出になる）。 */}
      {item.fields.map((field) => (
        <div className="stack" key={field.name}>
          <label htmlFor={`setting-${item.cloud}-${field.name}`}>{field.label}</label>
          {field.multiline === true ? (
            <textarea
              id={`setting-${item.cloud}-${field.name}`}
              rows={4}
              autoComplete="off"
              value={values[field.name] ?? ""}
              onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
            />
          ) : (
            <input
              id={`setting-${item.cloud}-${field.name}`}
              // ★ 秘密は打っている最中も肩越しに見えないようにする。
              type={field.secret ? "password" : "text"}
              autoComplete={field.secret ? "new-password" : "off"}
              value={values[field.name] ?? ""}
              onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
            />
          )}
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {field.hint}
            {field.secret && "（★ 入れたあとは読み返せません）"}
          </p>
        </div>
      ))}

      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="primary"
          disabled={busy || !ready}
          onClick={() => {
            onSave(
              item.cloud,
              Object.fromEntries(
                item.fields.map((field) => [
                  field.name,
                  // ★ 秘密は 1 文字も触らない。他は前後の空白を落とす。
                  field.secret ? (values[field.name] ?? "") : (values[field.name] ?? "").trim(),
                ]),
              ),
            );
            // ★ 画面に資格を残さない。
            setValues({});
          }}
        >
          保存
        </button>
        {item.configured && (
          <button type="button" disabled={busy} onClick={() => onRemove(item.cloud)}>
            外す
          </button>
        )}
      </div>

      {item.configured && (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          外すと、この置き場所はお客様の選択肢から消えます。
        </p>
      )}
    </section>
  );
}
